import { z } from 'zod';

/**
 * Deterministic in-app navigation target for an inbox CTA.
 *
 * Closed union by construction (D-02 style): only surfaces the web-chat can
 * route to today — a trip page, a voucher inside whichever trip holds it, the
 * approvals area, the requested-trips board, a conversation thread. `conversation` doubles as the
 * universal fallback: every inbox item carries a `conversationId`, so a CTA
 * can always land somewhere even when no richer target is derivable from the
 * producer's metadata.
 *
 * `voucher` exists because a booking notification is emitted BEFORE the trip
 * it belongs to is materialized (trip sync is a separate consumer), so the
 * producer knows the voucher code but not the trip id: the client resolves
 * the code against the traveler's own trips and opens that trip's service
 * drawer (`/trips/:id?voucher=`), falling back to the home when none holds it.
 */
export const NotificationNavigateTargetSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('trip'), tripId: z.string().min(1) }).strict(),
  z.object({ kind: z.literal('voucher'), voucherCode: z.string().min(1) }).strict(),
  // `voucherCode` optionally pre-focuses one voucher inside the approvals
  // area; absent means "open the approvals list" (e.g. trip-batched
  // approvals, where no single voucher is THE subject).
  z.object({ kind: z.literal('approvals'), voucherCode: z.string().optional() }).strict(),
  // The board of trips the user booked for others: a requester reads a
  // traveler's trip there, since the trip route serves only the trip's owner.
  z.object({ kind: z.literal('requested') }).strict(),
  z.object({ kind: z.literal('conversation'), conversationId: z.string().min(1) }).strict(),
  // The traveler's invitation to an event (`/event-invites/:eventId`).
  z.object({ kind: z.literal('event_invite'), eventId: z.string().min(1) }).strict(),
]);
export type NotificationNavigateTarget = z.infer<typeof NotificationNavigateTargetSchema>;

/**
 * Inbox call-to-action. `label` is localized ON THE SERVER (the conversation
 * service renders it with the same locale resolved for the inbox
 * title/detail), so clients render it verbatim — no client-side i18n lookup
 * for CTA text.
 *
 * `effect` is a closed discriminated union:
 * - `chat` seeds the composer of `conversationId` with a localized `prompt`
 *   (the user still sends it — the CTA never speaks for the user);
 * - `navigate` routes to a deterministic in-app target (see above).
 */
export const NotificationCtaSchema = z
  .object({
    label: z.string().min(1),
    effect: z.discriminatedUnion('kind', [
      z
        .object({
          kind: z.literal('chat'),
          conversationId: z.string().min(1),
          prompt: z.string().min(1),
        })
        .strict(),
      z.object({ kind: z.literal('navigate'), target: NotificationNavigateTargetSchema }).strict(),
    ]),
  })
  .strict();
export type NotificationCta = z.infer<typeof NotificationCtaSchema>;
